import {
  CallSplit as CallSplitIcon,
  CloudDownloadOutlined as CloudDownloadIcon,
  CloudUploadOutlined as CloudUploadIcon,
  DeleteSweep as DeleteSweepIcon,
  SmartToyOutlined as BotIcon,
} from "@mui/icons-material";
import type { ReactElement } from "react";

import type { MetaTone } from "./MetaPill";

/** The bot scope of a major job (MajorJob.bot_kind). */
export type BotKind = "download_bots" | "upload_bots" | "delete_bots" | "split";

export interface BotKindOption {
  value: BotKind;
  label: string;
  hint: string;
}

export const BOT_KIND_OPTIONS: BotKindOption[] = [
  {
    value: "download_bots",
    label: "Download",
    hint: "Bots that download the vendor's documents into Data Storage.",
  },
  {
    value: "upload_bots",
    label: "Upload",
    hint: "Bots that upload documents from Data Storage into the vendor's portal.",
  },
  {
    value: "delete_bots",
    label: "Delete",
    hint: "Bots that delete documents of the listed types from the vendor's portal.",
  },
  {
    value: "split",
    label: "Split",
    hint:
      "Split the vendor's multi-employee PDFs into one folder per employee and save them " +
      "to Data Storage. When the vendor's Download major job has collected the SSN file, the " +
      "saved folders can be linked to it.",
  },
];

export interface BotKindMeta {
  label: string;
  tone: MetaTone;
  /** Theme color for text: `${tone}.main`, or text.secondary for an unknown kind. */
  color: string;
  icon: ReactElement;
  known: boolean;
}

const KNOWN: Record<BotKind, { label: string; tone: MetaTone; icon: () => ReactElement }> = {
  download_bots: { label: "Download", tone: "info", icon: () => <CloudDownloadIcon /> },
  upload_bots: { label: "Upload", tone: "success", icon: () => <CloudUploadIcon /> },
  delete_bots: { label: "Delete", tone: "error", icon: () => <DeleteSweepIcon /> },
  split: { label: "Split", tone: "warning", icon: () => <CallSplitIcon /> },
};

function humanize(kind: string): string {
  return kind
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Label, tone and icon of a bot scope, the same everywhere it is shown. A kind
 * this build does not know is shown as itself - never as Download.
 */
export function botKindMeta(kind: string | null | undefined): BotKindMeta {
  const value = (kind ?? "").trim();
  const known = KNOWN[value as BotKind];
  if (known) {
    return {
      label: known.label,
      tone: known.tone,
      color: `${known.tone}.main`,
      icon: known.icon(),
      known: true,
    };
  }
  return {
    label: humanize(value),
    tone: "neutral",
    color: "text.secondary",
    icon: <BotIcon />,
    known: false,
  };
}

export const botKindLabel = (kind: string | null | undefined): string =>
  botKindMeta(kind).label;

export const isSplitKind = (kind: string | null | undefined): boolean => kind === "split";
export const isUploadKind = (kind: string | null | undefined): boolean => kind === "upload_bots";
export const isDeleteKind = (kind: string | null | undefined): boolean => kind === "delete_bots";
